// @flow

import * as React from 'react';
import { Button } from '@material-ui/core';
import { Flex, Box } from 'react-system';
import { graphql, useFragment, useMutation } from 'react-relay';
import { Email } from '../../icons/email';
import { useLocale } from '../../hooks/locale';
import { useRouter } from '../../controls/router';
import { InlineMarked, Marked } from '../../controls/marked';
import { BubbleBox, BubblesSeq } from '../../controls/bubble';
import { RadioCard, RadioCardGroup } from '../../controls/radio-card';
import { useAnalytics } from '../../controls/analytics';
import { Alert } from '../../controls/Alert';
import type { DevelopmentConfirmationStep_form$key } from './__generated__/DevelopmentConfirmationStep_form.graphql';
import type { DevelopmentConfirmationStepUpsertFormMutation } from './__generated__/DevelopmentConfirmationStepUpsertFormMutation.graphql';

type Props = {|
  form: DevelopmentConfirmationStep_form$key,
|};

const useOptimisticUpsertForm = ({ form }) => {
  const [upsertForm, upserting] =
    useMutation<DevelopmentConfirmationStepUpsertFormMutation>(
      graphql`
        mutation DevelopmentConfirmationStepUpsertFormMutation(
          $input: UpsertDevelopmentRegistrationFormInput!
        ) {
          upsertDevelopmentRegistrationForm(input: $input) {
            form: developmentRegistrationForm {
              id
              ...DevelopmentConfirmationStep_form @relay(mask: false)
            }
          }
        }
      `,
    );
  const upsertOptimisticForm = formShape => {
    const optimisticResponse = {
      upsertDevelopmentRegistrationForm: {
        form: {
          id: form.id,
          developmentProduct: form.developmentProduct,
          ...formShape,
        },
      },
    };
    upsertForm({
      variables: {
        input: {
          developmentRegistrationForm: { id: form.id, ...formShape },
        },
      },
      optimisticResponse,
    });
  };
  return [upsertOptimisticForm, upserting];
};

const ResultSection = ({ form }) => {
  const { t } = useLocale();
  const [optimisticUpsertForm] = useOptimisticUpsertForm({ form });
  const { hasLandForSale } = form;
  return (
    <BubblesSeq animate={form.hasLandForSale == null}>
      <Alert>
        <Flex alignItems={'center'} flexDirection={'column'}>
          <Email size={20} />
          {t('registrationComplete')}
        </Flex>
      </Alert>
      <BubbleBox>
        <InlineMarked>
          {t('thankYouForRegistration', {
            title: form.developmentProduct?.title ?? '',
          })}
        </InlineMarked>
      </BubbleBox>
      <BubbleBox>
        <Marked>{t('increaseYourChanceOfBeingSelected')}</Marked>
      </BubbleBox>
      <Box>
        <Box py={3}>{t('hasLandForSale')}</Box>
        <RadioCardGroup>
          <RadioCard
            checked={hasLandForSale === true}
            onChange={() => {
              optimisticUpsertForm({
                hasLandForSale: true,
              });
            }}
          >
            {t('yes')}
          </RadioCard>
          <RadioCard
            checked={hasLandForSale === false}
            onChange={() => {
              optimisticUpsertForm({
                hasLandForSale: false,
              });
            }}
          >
            {t('no')}
          </RadioCard>
        </RadioCardGroup>
      </Box>
    </BubblesSeq>
  );
};

const ConclusionSection = ({ form }) => {
  const { t } = useLocale();
  const router = useRouter();
  return (
    <BubblesSeq animate={true}>
      <BubbleBox>
        <InlineMarked>{t('resultSectionCompleteMessage')}</InlineMarked>
      </BubbleBox>
      <Box py={2}>
        <Button
          color="primary"
          size="large"
          onClick={() => {
            router.push({
              pathname: '/development',
              query: {
                slug: form.developmentProduct?.slug ?? '',
              },
            });
          }}
        >
          {t('backToDevelopment')}
        </Button>
      </Box>
    </BubblesSeq>
  );
};

export const DevelopmentConfirmationStep = (props: Props): React.Node => {
  const form = useFragment(
    graphql`
      fragment DevelopmentConfirmationStep_form on DevelopmentRegistrationForm {
        id
        hasLandForSale
        developmentProduct {
          id
          slug
          title
        }
      }
    `,
    props.form,
  );
  const { track } = useAnalytics();

  const calledOnce = React.useRef(false);
  React.useEffect(() => {
    if (calledOnce.current === false) {
      calledOnce.current = true;
      track('RA.Web.Development.Registration.Result.Displayed', {
        registration_form_id: form.id,
      });
    }
  });

  return (
    <>
      <ResultSection form={form} />
      {form.hasLandForSale != null && <ConclusionSection form={form} />}
    </>
  );
};
